import { useEffect } from 'react';
import { format, parseISO } from 'date-fns';

import { usePharmacyWarnings } from '@/lib/api/adminHooks';
import type { PharmacyWarningRow } from '@/types/admin';

/**
 * Patient details modal — opened from a patient name on a warning card.
 *
 * Shows the patient's basic identifiers + a Healthie deep-link (when
 * linked) + every warning we have on file for this patient (resolved
 * and unresolved), so the admin can see the full pharmacy context for
 * one click of action.
 *
 * Re-fetches with patient_id filter so the modal isn't constrained to
 * the warnings already loaded on the current tab.
 */
export function PatientWarningsModal({
  seed,
  onClose,
}: {
  seed: PharmacyWarningRow;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error } = usePharmacyWarnings(
    'all',
    seed.patient_id ?? undefined,
  );

  // Close on Esc.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const fullName =
    [seed.patient_first_name, seed.patient_last_name].filter(Boolean).join(' ') ||
    seed.patient_email ||
    seed.patient_id ||
    'Patient';

  const warnings = data?.warnings ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-24 bg-ink/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white border border-default shadow-xl w-full max-w-content-md flex flex-col gap-16 p-15 my-24"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="type-heading-lg">{fullName}</h2>
            <p className="type-body-sm text-ink-muted">
              {seed.patient_email || '(no email on file)'}
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <DetailGrid w={seed} />

        <div className="flex flex-col gap-8">
          <p className="type-label-sm text-ink-muted">Pharmacy warning history</p>
          {isLoading ? (
            <p className="type-body-sm text-ink-muted">Loading…</p>
          ) : isError ? (
            <p className="type-body-sm text-status-danger">
              Failed to load: {String((error as Error)?.message || error)}
            </p>
          ) : warnings.length === 0 ? (
            <p className="type-body-sm text-ink-muted">
              No pharmacy warnings on record for this patient.
            </p>
          ) : (
            <WarningHistoryList warnings={warnings} />
          )}
        </div>

        {data ? (
          <p className="type-body-xs text-ink-muted">
            {data.unresolved_count} unresolved · {data.resolved_count} resolved
            for this patient
          </p>
        ) : null}
      </div>
    </div>
  );
}

function DetailGrid({ w }: { w: PharmacyWarningRow }) {
  const healthieHref = w.patient_healthie_id
    ? `https://secure.gethealthie.com/users/${w.patient_healthie_id}`
    : null;
  return (
    <div className="grid grid-cols-2 gap-x-16 gap-y-6">
      <Field label="Patient ID" value={w.patient_id || '—'} mono />
      <Field
        label="Healthie ID"
        value={
          healthieHref ? (
            <a
              href={healthieHref}
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline type-mono-sm"
            >
              {w.patient_healthie_id} ↗
            </a>
          ) : (
            <span className="text-ink-muted">— not linked</span>
          )
        }
      />
      <Field label="Email" value={w.patient_email || '—'} />
      <Field label="Partner" value={w.partner_key} />
      {w.current_address?.line1 ? (
        <Field
          label="Last known address"
          value={
            <span>
              {w.current_address.line1}
              {w.current_address.line2 ? `, ${w.current_address.line2}` : ''}
              <br />
              {w.current_address.city}, {w.current_address.state}{' '}
              {w.current_address.postal_code}
            </span>
          }
          colSpan={2}
        />
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  colSpan,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  colSpan?: number;
}) {
  const cls = colSpan === 2 ? 'col-span-2 flex flex-col gap-2' : 'flex flex-col gap-2';
  return (
    <div className={cls}>
      <span className="type-label-xs text-ink-muted">{label}</span>
      <span
        className={mono ? 'type-mono-sm text-ink' : 'type-body-sm text-ink'}
      >
        {value}
      </span>
    </div>
  );
}

function WarningHistoryList({
  warnings,
}: {
  warnings: PharmacyWarningRow[];
}) {
  return (
    <ul className="flex flex-col gap-6">
      {warnings.map((w) => (
        <li
          key={`${w.order_id}:${w.reason_code}:${w.received_at}:${w.resolved_at ?? 'open'}`}
          className="rounded-sm border border-subtle p-10 flex flex-col gap-2"
        >
          <div className="flex items-baseline justify-between gap-12">
            <div className="flex items-center gap-6 flex-wrap">
              <span className="type-label-sm text-ink-emphasis bg-surface-soft border border-default rounded-sm px-4 py-1">
                {w.reason_code ?? '?'}
              </span>
              <span className="type-body-sm text-ink">
                {w.reason_description || 'no description'}
              </span>
              {w.resolved_at ? (
                <span className="type-label-xs text-status-success">
                  resolved
                </span>
              ) : (
                <span className="type-label-xs text-status-warning">
                  unresolved
                </span>
              )}
            </div>
            <span className="type-body-xs text-ink-muted whitespace-nowrap">
              {w.order_number || '—'} ·{' '}
              {w.received_at
                ? format(parseISO(w.received_at), 'MMM d')
                : '—'}
            </span>
          </div>
          {w.notes ? (
            <p className="type-body-xs text-ink-muted">"{w.notes}"</p>
          ) : null}
          {w.resolved_at && w.resolved_via ? (
            <p className="type-body-xs text-ink-muted">
              Resolved{' '}
              {format(parseISO(w.resolved_at), 'MMM d, h:mm a')} via{' '}
              <code className="type-mono-sm">{w.resolved_via}</code>
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
